/**
 * Alternate contact management for authentication orchestrator
 * Handles adding alternate email/phone, account discovery, and conflict resolution
 */

import type {
  AuthCheckUserExistsQuery,
  AuthCheckMergeEligibilityQuery,
  AuthLinkEmailMutation,
  AuthLinkPhoneMutation,
  CheckUserExistsInput,
  CheckMergeEligibilityInput
} from '@/generated/nexus-graphql';
import { formatPhoneToE164 } from '@/utils/phone-formatter';
import type { AuthOrchestratorState } from './useAuthState';
import type { NavigationService } from './useAuthNavigationService';
import { ERROR_MESSAGES } from '../constants';
import { createDebugLogger } from '../utils/debug';

const logger = createDebugLogger('AlternateContact');

interface LinkEmailInput {
  input: {
    email: string;
    otpCode?: string;
    forceMerge?: boolean;
  };
}

interface LinkPhoneInput {
  input: {
    phoneNumber: string;
    otpCode?: string;
    forceMerge?: boolean;
  };
}

export function useAuthAlternateContact(
  state: AuthOrchestratorState,
  deps: {
    checkUserExistsQuery: (params: { input: CheckUserExistsInput }) => Promise<{
      data?: AuthCheckUserExistsQuery;
      errors?: Array<{ message: string }>;
    }>;
    checkMergeEligibilityQuery?: (params: { input: CheckMergeEligibilityInput }) => Promise<{
      data?: AuthCheckMergeEligibilityQuery;
      errors?: Array<{ message: string }>;
    }>;
    linkEmailMutate: (params: LinkEmailInput) => Promise<{
      data?: AuthLinkEmailMutation;
      errors?: Array<{ message: string }>;
    }>;
    linkPhoneMutate: (params: LinkPhoneInput) => Promise<{
      data?: AuthLinkPhoneMutation;
      errors?: Array<{ message: string }>;
    }>;
    authFlowHandlers?: { refresh: () => Promise<void> };
    isProfileComplete?: { value: boolean };
    nextPhase: () => void;
    completeAndRedirect?: () => void;
    navigationService?: NavigationService;
    getUser?: { value: any };
  }
) {
  /**
   * The contact belongs to nobody else: LINK it to this account (backend sends a
   * one-time code when it needs to, and the identityLinkingOtp phase finishes the link
   * and continues). Before this, the happy path only checked for a conflict and called
   * nextPhase(), so the phone typed at "Add Mobile Phone" was never stored anywhere -
   * the Start form, Calendly and VideoAsk each asked for it again (prod, 2026-09-12).
   */
  const linkNewAlternateContact = async (normalizedContact: string, isEmail: boolean) => {
    state.alternateContactSkipped = false;
    const type = isEmail ? 'email' : 'phone';
    const value = isEmail ? normalizedContact : formatPhoneToE164(normalizedContact);
    state.pendingIdentityLink = { type, value, otpSent: false };

    const result = isEmail
      ? await deps.linkEmailMutate({ input: { email: value } })
      : await deps.linkPhoneMutate({ input: { phoneNumber: value } });

    if (result?.errors) {
      throw new Error(result.errors[0]?.message || ERROR_MESSAGES.GENERIC);
    }
    const linkResult: any = isEmail
      ? (result?.data as any)?.authLinkEmail
      : (result?.data as any)?.authLinkPhone;

    if (linkResult?.requiresVerification) {
      state.pendingIdentityLink.otpSent = true;
      if (deps.navigationService) {
        await deps.navigationService.navigate('identityLinkingOtp');
      } else {
        state.phase = 'identityLinkingOtp';
      }
      return;
    }
    if (linkResult?.conflict) {
      state.pendingIdentityLink.conflictingUserId = linkResult.conflictUserId;
      state.pendingIdentityLink.canMerge = linkResult.canMerge;
      state.error = linkResult.message || ERROR_MESSAGES.IDENTITY_CONFLICT;
      if (deps.navigationService) {
        await deps.navigationService.navigate('identityConflict');
      } else {
        state.phase = 'identityConflict';
      }
      return;
    }
    if (!linkResult?.success) {
      throw new Error(linkResult?.message || ERROR_MESSAGES.GENERIC);
    }
    // Linked without a code (backend already trusts this contact)
    logger.log('Alternate contact linked, proceeding to profile');
    state.pendingIdentityLink = { type: null, value: '', otpSent: false };
    deps.nextPhase();
  };

  /**
   * Submit alternate contact to check for existing account
   */
  const submitAlternateContact = async (contactValue: string) => {
    logger.log('Checking alternate contact for existing account');
    state.loading = true;
    state.error = null;
    state.alternateContactValue = contactValue;

    try {
      // Check if this contact belongs to an existing account
      const isEmail = !state.isEmail; // Alternate is opposite of login method
      const normalizedContact = isEmail ? contactValue : contactValue.replace(/\D/g, '');

      // Check if we have the new merge eligibility query available
      if (deps.checkMergeEligibilityQuery) {
        // Use the new merge eligibility query for better detection
        const currentUser = deps.getUser?.value;
        const eligibilityResult = await deps.checkMergeEligibilityQuery({
          input: {
            identifier: normalizedContact,
            type: isEmail ? 'EMAIL' : 'PHONE',
            currentUserId: currentUser?.id
          }
        });

        // Check for GraphQL errors first
        if (eligibilityResult?.errors) {
          const errorMessage = eligibilityResult.errors[0]?.message || ERROR_MESSAGES.NETWORK_ERROR;
          logger.error('GraphQL error in checkMergeEligibility:', errorMessage);
          throw new Error(errorMessage);
        }

        const eligibility = eligibilityResult?.data?.authCheckMergeEligibility;

        if (eligibility?.exists) {
          if (!eligibility.canMerge) {
            // Different user owns this identity - cannot merge
            logger.error(`Cannot link: ${eligibility.message}`);
            state.error = eligibility.message;
            return;
          }

          // Same user owns this identity - show account discovery
          logger.log('Identity belongs to existing account - showing account discovery');
          state.discoveredAccountId = eligibility.conflictUserId || 'existing-account';
          state.pendingIdentityLink = {
            type: isEmail ? 'email' : 'phone',
            value: normalizedContact,
            otpSent: false
          };
          if (deps.navigationService) {
            await deps.navigationService.navigate('accountDiscovery');
          } else {
            state.phase = 'accountDiscovery'; // Fallback
          }
        } else {
          // No existing account: link it to THIS account (OTP if the backend asks)
          logger.log('No existing account found, linking alternate contact');
          await linkNewAlternateContact(normalizedContact, isEmail);
        }
      } else {
        // Fallback to old checkUserExists query
        const result = await deps.checkUserExistsQuery({
          input: { identifier: normalizedContact, type: isEmail ? 'EMAIL' : 'PHONE' }
        });

        // Check for GraphQL errors first
        if (result?.errors) {
          const errorMessage = result.errors[0]?.message || ERROR_MESSAGES.NETWORK_ERROR;
          logger.error('GraphQL error in checkUserExists:', errorMessage);
          throw new Error(errorMessage);
        }

        const userExists = result?.data?.authCheckUserExists;

        if (userExists) {
          // Found existing account!
          logger.log('Discovered existing account with this contact');

          // IMPORTANT: Without merge eligibility check, we can't know if accounts can merge
          // We'll proceed to account discovery and let the backend provide the answer

          state.discoveredAccountId = 'existing-account';
          state.pendingIdentityLink = {
            type: isEmail ? 'email' : 'phone',
            value: normalizedContact,
            otpSent: false
          };
          if (deps.navigationService) {
            await deps.navigationService.navigate('accountDiscovery');
          } else {
            state.phase = 'accountDiscovery'; // Fallback
          }
        } else {
          // No existing account: link it to THIS account (OTP if the backend asks)
          logger.log('No existing account found, linking alternate contact');
          await linkNewAlternateContact(normalizedContact, isEmail);
        }
      }
    } catch (error) {
      logger.error('Failed to check alternate contact:', error);
      state.error = ERROR_MESSAGES.GENERIC;
    } finally {
      state.loading = false;
    }
  };

  /**
   * Skip adding alternate contact
   */
  const skipAlternateContact = () => {
    logger.log('User skipped alternate contact');
    state.alternateContactSkipped = true;
    deps.nextPhase();
  };

  /**
   * Add current identity to existing account (merge accounts)
   * This links the CURRENT login method to the EXISTING account found
   */
  const addToExistingAccount = async (existingAccountId: string) => {
    logger.log('Linking current identity to existing account');
    state.loading = true;
    state.error = null;

    try {
      // Important: We're linking the CURRENT identity (what user logged in with)
      // to the EXISTING account (what was found via alternate contact)
      const currentIsEmail = state.isEmail; // What the user logged in with

      let result;
      if (currentIsEmail) {
        // User logged in with email, found existing phone account
        // We need to link the EMAIL to the PHONE account
        logger.log('Linking current email to existing phone account');
        result = await deps.linkEmailMutate({
          input: {
            email: state.contact, // Current email they logged in with
            otpCode: '', // Will trigger OTP send if needed
            forceMerge: true // Force merge with existing phone account
          }
        });

        // First check if the operation was successful
        if (!result.data?.authLinkEmail?.success) {
          const message = result.data?.authLinkEmail?.message || '';
          logger.error('Failed to link email:', message);

          // Check for specific error scenarios
          if (
            message.includes('already has an email') ||
            message.includes('Each account can only have one email')
          ) {
            // The phone account already has an email address - accounts are incompatible
            state.error =
              'This phone number is already linked to another email account. Please use a different phone number or continue without adding a phone.';
          } else if (message.includes('already linked') || message.includes('already associated')) {
            // Generic already linked message
            state.error =
              'This phone number is already associated with another account. Please use a different phone number.';
          } else {
            // Generic error
            state.error = message || 'Failed to link accounts. Please try again.';
          }
          return;
        }

        // Check for conflicts that prevent merging
        if (result.data?.authLinkEmail?.conflict) {
          logger.warn('Conflict detected during email linking');
          if (result.data?.authLinkEmail?.canMerge === false) {
            // Cannot merge - show appropriate error
            state.error =
              'These accounts cannot be merged. The phone number may already be linked to another email account.';
            return;
          }
          // If canMerge is true, continue to check other conditions
        }

        if (result.data?.authLinkEmail?.mergedAccountId) {
          // Accounts were merged!
          logger.log('Accounts successfully merged:', result.data.authLinkEmail.mergedAccountId);

          // Track the merge
          if (state.discoveredAccountId) {
            state.resolvedConflicts.add(state.discoveredAccountId);
          }
          state.passedGates.add('accountDiscovery');

          // The user is now signed into the merged account
          // Refresh auth state to get updated user info
          if (deps.authFlowHandlers) {
            await deps.authFlowHandlers.refresh();
          }

          // Clear discovered account and continue
          state.discoveredAccountId = null;
          state.alternateContactSkipped = false;

          // Check if profile is now complete after merge
          if (deps.isProfileComplete?.value) {
            if (deps.navigationService) {
              await deps.navigationService.completeFlow();
            } else {
              state.phase = 'complete'; // Fallback
            }
            if (deps.completeAndRedirect) {
              deps.completeAndRedirect();
            }
          } else {
            deps.nextPhase(); // Continue to profile setup
          }
        } else if (result.data?.authLinkEmail?.requiresVerification) {
          // Need OTP verification
          logger.log('OTP verification required for email linking');
          state.pendingIdentityLink = {
            type: 'email',
            value: state.alternateContactValue,
            otpSent: true
          };
          if (deps.navigationService) {
            await deps.navigationService.navigate('identityLinkingOtp');
          } else {
            state.phase = 'identityLinkingOtp'; // Fallback
          }
        } else {
          // Operation succeeded but no merge or verification needed
          // This might mean accounts are already linked or the operation was a no-op
          logger.log(
            'Link operation completed without merge or verification - accounts may already be linked'
          );
          state.discoveredAccountId = null;
          state.alternateContactSkipped = false;

          // Continue to the next phase
          deps.nextPhase();
        }
      } else {
        // User logged in with phone, found existing email account
        // We need to link the PHONE to the EMAIL account
        logger.log('Linking current phone to existing email account');
        result = await deps.linkPhoneMutate({
          input: {
            phoneNumber: state.contact, // Current phone they logged in with
            otpCode: '', // Will trigger OTP send if needed
            forceMerge: true // Force merge with existing email account
          }
        });

        // First check if the operation was successful
        if (!result.data?.authLinkPhone?.success) {
          const message = result.data?.authLinkPhone?.message || '';
          logger.error('Failed to link phone:', message);

          // Check for specific error scenarios
          if (
            message.includes('already has a phone') ||
            message.includes('Each account can only have one phone')
          ) {
            // The email account already has a phone number - accounts are incompatible
            state.error =
              'This email is already linked to another phone number. Please use a different email or continue without adding an email.';
          } else if (message.includes('already linked') || message.includes('already associated')) {
            // Generic already linked message
            state.error =
              'This email is already associated with another account. Please use a different email.';
          } else {
            // Generic error
            state.error = message || 'Failed to link accounts. Please try again.';
          }
          return;
        }

        // Check for conflicts that prevent merging
        if (result.data?.authLinkPhone?.conflict) {
          logger.warn('Conflict detected during phone linking');
          if (result.data?.authLinkPhone?.canMerge === false) {
            // Cannot merge - show appropriate error
            state.error =
              'These accounts cannot be merged. The email may already be linked to another phone number.';
            return;
          }
          // If canMerge is true, continue to check other conditions
        }

        if (result.data?.authLinkPhone?.mergedAccountId) {
          // Accounts were merged!
          logger.log('Accounts successfully merged:', result.data.authLinkPhone.mergedAccountId);

          // Track the merge
          if (state.discoveredAccountId) {
            state.resolvedConflicts.add(state.discoveredAccountId);
          }
          state.passedGates.add('accountDiscovery');

          // The user is now signed into the merged account
          // Refresh auth state to get updated user info
          if (deps.authFlowHandlers) {
            await deps.authFlowHandlers.refresh();
          }

          // Clear discovered account and continue
          state.discoveredAccountId = null;
          state.alternateContactSkipped = false;

          // Check if profile is now complete after merge
          if (deps.isProfileComplete?.value) {
            if (deps.navigationService) {
              await deps.navigationService.completeFlow();
            } else {
              state.phase = 'complete'; // Fallback
            }
            if (deps.completeAndRedirect) {
              deps.completeAndRedirect();
            }
          } else {
            deps.nextPhase(); // Continue to profile setup
          }
        } else if (result.data?.authLinkPhone?.requiresVerification) {
          // Need OTP verification
          logger.log('OTP verification required for phone linking');
          state.pendingIdentityLink = {
            type: 'phone',
            value: state.alternateContactValue,
            otpSent: true
          };
          if (deps.navigationService) {
            await deps.navigationService.navigate('identityLinkingOtp');
          } else {
            state.phase = 'identityLinkingOtp'; // Fallback
          }
        } else {
          // Operation succeeded but no merge or verification needed
          // This might mean accounts are already linked or the operation was a no-op
          logger.log(
            'Link operation completed without merge or verification - accounts may already be linked'
          );
          state.discoveredAccountId = null;
          state.alternateContactSkipped = false;

          // Continue to the next phase
          deps.nextPhase();
        }
      }
    } catch (error: any) {
      logger.error('Failed to add to existing account:', error);

      // Check for specific error types
      const errorMessage = error?.message || '';

      if (errorMessage.includes('already linked') || errorMessage.includes('already has')) {
        // The account already has both email and phone
        state.error =
          'This contact is already linked to another account with both email and phone. Please use a different contact.';
      } else if (errorMessage.includes('conflict')) {
        // General conflict error
        state.error =
          'Unable to link accounts due to a conflict. Please contact support if this persists.';
      } else {
        // Generic error
        state.error = ERROR_MESSAGES.GENERIC;
      }
    } finally {
      state.loading = false;
    }
  };

  /**
   * User wants to use a different contact
   */
  const useDifferentContact = async () => {
    logger.log('User wants to use different contact');
    state.discoveredAccountId = null;
    state.alternateContactValue = '';
    if (deps.navigationService) {
      await deps.navigationService.navigate('alternateContact');
    } else {
      state.phase = 'alternateContact'; // Fallback
    }
  };

  /**
   * Keep accounts separate (don't merge)
   */
  const keepAccountsSeparate = () => {
    logger.log('User chose to keep accounts separate');

    // Track the decision to keep accounts separate
    if (state.discoveredAccountId) {
      state.resolvedConflicts.add(state.discoveredAccountId);
    }
    state.passedGates.add('accountDiscovery');

    // Clear discovered account
    state.discoveredAccountId = null;
    state.alternateContactSkipped = true;

    // Continue to profile setup
    deps.nextPhase();
  };

  /**
   * Retry with different contact when conflict occurs
   */
  const retryWithDifferentContact = async () => {
    logger.log('Retrying with different contact');
    // Clear pending link and go back to alternateContact phase
    state.pendingIdentityLink = { type: null, value: '', otpSent: false };
    if (deps.navigationService) {
      await deps.navigationService.navigate('alternateContact');
    } else {
      state.phase = 'alternateContact'; // Fallback
    }
    state.error = null;
    state.alternateContactValue = ''; // Clear the previous value
  };

  /**
   * Skip adding conflicting contact and continue
   */
  const skipConflictingContact = () => {
    logger.log('Skipping conflicting contact');
    // Clear pending link and continue to next phase
    state.pendingIdentityLink = { type: null, value: '', otpSent: false };
    state.alternateContactSkipped = true;
    state.error = null;
    deps.nextPhase();
  };

  return {
    submitAlternateContact,
    skipAlternateContact,
    addToExistingAccount,
    useDifferentContact,
    keepAccountsSeparate,
    retryWithDifferentContact,
    skipConflictingContact
  };
}
